// IT reviewer's passwordless page — scoped to ONE agreement. No Main login, no matters.
// Shows the agreement + our automated IT read (with evidence excerpts), lets the reviewer
// paste his own standards, and chat about it (ZDR, single-agreement corpus). The token in
// the URL is the credential. (Ken 2026-10-13)
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { Components } from 'react-markdown'
import { itReview, type ITReviewState } from '../api'

type Msg = { role: 'user' | 'assistant'; content: string }
// Fill token (Ken 2026-10-22 D). Public/un-themed page → defaults to the blue
// accent, so the white bubble text stays correct here; a themed tenant with a
// pale accent-bg would want dark text, but this page is never themed.
const USER_BG = 'var(--me-accent-bg)'
const MD: Components = {
  table: (p) => <table className="me-aq-table" style={{ borderCollapse: 'collapse', width: '100%', margin: '2px 0' }} {...p} />,
  th: (p) => <th style={{ textAlign: 'left', fontWeight: 600, padding: '2px 14px 6px 0', verticalAlign: 'top', color: 'var(--me-textMute)', borderBottom: '1px solid var(--me-border)' }} {...p} />,
  td: (p) => <td style={{ textAlign: 'left', padding: '6px 14px 6px 0', verticalAlign: 'top' }} {...p} />,
}
const SIGNAL_LABEL: Record<string, string> = {
  systems_access: 'Systems access', externally_hosted: 'Externally hosted', sensitive_data: 'Sensitive data',
}

export default function ITReview() {
  const { token = '' } = useParams()
  const [state, setState] = useState<ITReviewState | null>(null)
  const [err, setErr] = useState('')
  const [standards, setStandards] = useState('')
  const [messages, setMessages] = useState<Msg[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    itReview.state(token).then(setState).catch(() => setErr('This link is not valid.'))
  }, [token])
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight) }, [messages, loading])

  const send = async (text: string) => {
    const q = text.trim()
    if (!q || loading) return
    const next = [...messages, { role: 'user' as const, content: q }]
    setMessages(next); setInput(''); setLoading(true)
    try {
      setMessages([...next, { role: 'assistant', content: await itReview.reply(token, next, standards) }])
    } catch {
      setMessages([...next, { role: 'assistant', content: '_Unavailable right now — please try again._' }])
    } finally { setLoading(false); inputRef.current?.focus() }
  }

  if (err) return <div className="mx-auto max-w-2xl p-10 me-text">{err}</div>
  if (!state) return <div className="mx-auto max-w-2xl p-10 me-text-mute">Loading…</div>

  const r = state.it_review
  const flags = (['systems_access', 'externally_hosted', 'sensitive_data'] as const).filter((k) => r[k])

  return (
    <div className="mx-auto max-w-3xl px-5 py-8" style={{ color: 'var(--me-text)' }}>
      <div className="text-[11px] uppercase tracking-wide me-text-mute">IT / Security Review</div>
      <h1 className="text-[20px] font-semibold mt-1">{state.agreement.name}</h1>
      {(state.agreement.counterparty || state.agreement.purpose) && (
        <div className="text-[13px] me-text-mute mt-1">
          {state.agreement.counterparty && <>Counterparty: {state.agreement.counterparty}. </>}
          {state.agreement.purpose}
        </div>
      )}

      {/* Our automated read + the evidence */}
      <div className="mt-5 rounded-lg p-4" style={{ background: 'var(--me-surface)', border: '1px solid var(--me-border)' }}>
        <div className="text-[13px] font-medium">
          Automated IT read: {r.required ? 'review recommended' : 'no IT flags'}
          {flags.length > 0 && <> — {flags.map((k) => SIGNAL_LABEL[k]).join(', ')}</>}
        </div>
        {r.rationale && <div className="text-[12.5px] me-text-mute mt-1">{r.rationale}</div>}
        {r.excerpts.length > 0 && (
          <div className="mt-3 space-y-2">
            <div className="text-[11px] uppercase tracking-wide me-text-mute">Evidence (from the agreement)</div>
            {r.excerpts.map((e, i) => (
              <div key={i} className="text-[13px]">
                <span className="me-text-mute">{SIGNAL_LABEL[e.signal] || 'Evidence'}:</span> “{e.quote}”
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reviewer's own standards */}
      <div className="mt-4">
        <div className="text-[12px] font-medium mb-1">Your standards (optional — paste or type; used as context)</div>
        <textarea value={standards} onChange={(e) => setStandards(e.target.value)} rows={4}
          placeholder="Paste your IT/security standards here…"
          className="w-full rounded-md px-3 py-2 text-[13px]"
          style={{ background: 'var(--me-surface)', border: '1px solid var(--me-border)', color: 'var(--me-text)' }} />
      </div>

      {/* Chat */}
      <div className="mt-5 rounded-lg flex flex-col" style={{ border: '1px solid var(--me-border)', height: '48vh' }}>
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {messages.length === 0 && (
            <div className="text-[13px] me-text-mute">
              Ask about this agreement — e.g. “Does this meet my standards?”, “Where is our data going?”,
              “What are the security obligations?”
            </div>
          )}
          {messages.map((m, i) => (
            m.role === 'user' ? (
              <div key={i} className="flex justify-end">
                <div className="rounded-lg px-3 py-2 text-[13.5px] whitespace-pre-wrap" style={{ background: USER_BG, color: '#fff', maxWidth: '80%' }}>{m.content}</div>
              </div>
            ) : (
              <div key={i} className="me-prose me-text break-words text-[13.5px]">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{m.content}</ReactMarkdown>
              </div>
            )
          ))}
          {loading && <div className="text-[13px] me-text-mute">Reading the agreement… (this can take ~30s)</div>}
        </div>
        <div className="flex items-center gap-2 px-3 py-3" style={{ borderTop: '1px solid var(--me-border)' }}>
          <input ref={inputRef} autoFocus value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(input) }}
            placeholder="Ask about this agreement…" className="flex-1 rounded-md px-3 py-2 text-[13.5px]"
            style={{ background: 'var(--me-surface)', border: '1px solid var(--me-border)', color: 'var(--me-text)' }} />
          <button onClick={() => send(input)} disabled={loading || !input.trim()}
            className="rounded-md px-3 py-2 text-[13px] font-medium"
            style={{ background: USER_BG, color: '#fff', opacity: loading || !input.trim() ? 0.5 : 1 }}>Send</button>
        </div>
      </div>
    </div>
  )
}
